import { useMemo, useState, type ReactNode } from "react";
import HeroCarousel from "../components/HeroCarousel";
import CategoryCard from "../components/CategoryCard";
import LandingIcon from "../components/LandingIcon";
import { LandingErrorState, LandingLoadingState } from "../components/LandingState";
import { LANDING_USE_MOCK } from "../config/landing.config";
import {
  LANDING_PAGE_GUTTER,
  LANDING_CATEGORY_GRID,
  LANDING_TYPOGRAPHY,
} from "../constants/landing.constants";
import { LANDING_CATEGORIES } from "../data/landing.mock";
import { useOurClasses } from "../hooks/useOurClasses";
import type { LandingCategory } from "../types/landing.types";

/**
 * Client-only filter for home category cards.
 * - Empty query → all categories unchanged
 * - Category title/description/badge match → keep full card
 * - Else keep card with only matching levels
 * Never hits the network.
 */
export function filterOurClassesCategories(
  categories: LandingCategory[],
  query: string
): LandingCategory[] {
  const q = query.trim().toLowerCase();
  if (!q) return categories;

  return categories.flatMap((category) => {
    const categoryMatch =
      category.title.toLowerCase().includes(q) ||
      category.description.toLowerCase().includes(q) ||
      (category.badge?.toLowerCase().includes(q) ?? false);

    if (categoryMatch) return [category];

    const levels = category.levels.filter((level) =>
      level.title.toLowerCase().includes(q)
    );
    if (levels.length === 0) return [];

    return [{ ...category, levels }];
  });
}

function OurClassesSection({
  search,
  onSearchChange,
  count,
  children,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  count: number;
  children: ReactNode;
}) {
  return (
    <section className={`pt-[52px] pb-16 ${LANDING_PAGE_GUTTER}`}>
      <div className={LANDING_TYPOGRAPHY.homeSectionHeader}>
        <h2 className={LANDING_TYPOGRAPHY.sectionTitleHome}>
          {count > 1
            ? `Our Classes(${String(count).padStart(2, "0")})`
            : "Our Class"}
        </h2>
        <div className={LANDING_TYPOGRAPHY.homeSearchWrap}>
          <LandingIcon
            name="search"
            className={LANDING_TYPOGRAPHY.filterSearchIcon}
          />
          <input
            type="search"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search classes"
            aria-label="Search classes"
            className={LANDING_TYPOGRAPHY.filterSearchInput}
          />
        </div>
      </div>
      {children}
    </section>
  );
}

const categoryGridClass = LANDING_CATEGORY_GRID;

function CategoryGrid({ categories }: { categories: LandingCategory[] }) {
  if (categories.length === 0) {
    return (
      <p className="py-16 text-center text-[16px] text-landing-muted">
        No classes match your search.
      </p>
    );
  }

  return (
    <div className={categoryGridClass}>
      {categories.map((category) => (
        <CategoryCard key={category.id} category={category} />
      ))}
    </div>
  );
}

function MockHomePage() {
  const [search, setSearch] = useState("");
  const filtered = useMemo(
    () => filterOurClassesCategories(LANDING_CATEGORIES, search),
    [search]
  );

  return (
    <OurClassesSection
      search={search}
      onSearchChange={setSearch}
      count={filtered.length}
    >
      <CategoryGrid categories={filtered} />
    </OurClassesSection>
  );
}

function ApiHomePage() {
  const { categories, isLoading, error, refetch } = useOurClasses();
  const [search, setSearch] = useState("");
  const filtered = useMemo(
    () => filterOurClassesCategories(categories, search),
    [categories, search]
  );

  return (
    <OurClassesSection
      search={search}
      onSearchChange={setSearch}
      count={filtered.length}
    >      {isLoading ? (
        <LandingLoadingState message="Loading classes…" />
      ) : error ? (
        <LandingErrorState message={error} onAction={refetch} />
      ) : categories.length === 0 ? (
        <p className="py-16 text-center text-[16px] text-landing-muted">
          No classes available right now.
        </p>
      ) : (
        <CategoryGrid categories={filtered} />
      )}
    </OurClassesSection>
  );
}

export default function HomePage() {
  return (
    <>
      <HeroCarousel />
      {LANDING_USE_MOCK ? <MockHomePage /> : <ApiHomePage />}
    </>
  );
}
